// Zustand store for global state management as specified in appmark.txt
import { create } from "zustand";
import { User } from "../types";

export { usePipelineBuilderStore } from "./pipelineBuilderStore";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

interface UIState {
  sidebarCollapsed: boolean;
  theme: "light" | "dark";
  toggleSidebar: () => void;
  toggleTheme: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

interface NotificationState {
  notifications: Array<{
    id: string;
    message: string;
    type: "info" | "success" | "warning" | "error";
    timestamp: Date;
  }>;
  addNotification: (
    message: string,
    type?: "info" | "success" | "warning" | "error"
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

// Auth store
export const useAuthStore = create<AuthState>((set) => {
  // Initialize from localStorage
  const token = localStorage.getItem("auth_token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  return {
    user,
    token,
    isAuthenticated: !!token,
    login: (token: string, user: User) => {
      localStorage.setItem("auth_token", token);
      localStorage.setItem("user", JSON.stringify(user));
      set({ token, user, isAuthenticated: true });
    },
    logout: () => {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      set({ token: null, user: null, isAuthenticated: false });
    },
  };
});

// UI store
export const useUIStore = create<UIState>((set, get) => {
  // Initialize sidebar state from localStorage
  const sidebarCollapsed = localStorage.getItem("sidebar_collapsed") === "true";
  const theme = (localStorage.getItem("theme") as "light" | "dark") || "dark";

  return {
    sidebarCollapsed,
    theme,
    toggleSidebar: () => {
      const newCollapsed = !get().sidebarCollapsed;
      localStorage.setItem("sidebar_collapsed", newCollapsed.toString());
      set({ sidebarCollapsed: newCollapsed });
    },
    toggleTheme: () => {
      const newTheme = get().theme === "light" ? "dark" : "light";
      localStorage.setItem("theme", newTheme);
      document.documentElement.classList.toggle("dark", newTheme === "dark");
      set({ theme: newTheme });
    },
    setSidebarCollapsed: (collapsed: boolean) => {
      localStorage.setItem("sidebar_collapsed", collapsed.toString());
      set({ sidebarCollapsed: collapsed });
    },
  };
});

// Notification store
export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  addNotification: (message: string, type = "info" as const) => {
    const notification = {
      id: Date.now().toString(),
      message,
      type,
      timestamp: new Date(),
    };
    set({ notifications: [...get().notifications, notification] });

    // Auto-remove after 5 seconds
    setTimeout(() => {
      get().removeNotification(notification.id);
    }, 5000);
  },
  removeNotification: (id: string) => {
    set({ notifications: get().notifications.filter((n) => n.id !== id) });
  },
  clearNotifications: () => {
    set({ notifications: [] });
  },
}));
